import { useCallback, useEffect, useMemo, useState } from 'react';

import { Pencil, Plus } from 'lucide-react';
import { Row, Col, FormGroup, Label, Switch, Button, SimpleAlert, Select, ButtonGroup, DefaultTooltip } from 'twyne-new.ui';

import CodeEditor from '@Components/form/CodeEditor';

import ConsentDBAs from '../../ConsentDBAs';

import { TcpaQuestionCustomProvider } from './tabs'
import { getBlocksByType } from './tabs/TemplateBuilder/Builder/utils/blocksDefinition';
import compiler from './tabs/TemplateBuilder/Builder/utils/compiler';

const CONSENT_POSITION_TYPES = [{
  label: 'After Answers',
  value: 'after',
}, {
  label: 'Before Answers',
  value: 'before',
}]

export default function TcpaQuestionCustomForm({ integrationData: { extraFields: customFields, tcpaquestioncustom = {} } = {}, disabled, onChange, onCustomFieldClick, onChangeContent, onCompilerError, integration }) {
  const [customFieldsOptions, setCustomFieldsOptions] = useState([])
  const [customFieldSelected, setCustomFieldSelected] = useState({})
  const { templateType, blocks: blocksData } = tcpaquestioncustom
  const { allowBidSelection } = integration?.configuration || {}

  useEffect(() => {
    if (!templateType) {
      onChange('templateType', 'button-and-dropdown')
    }
  }, [templateType, onChange])

  const { blocks } = useMemo(() => getBlocksByType(templateType || 'button-and-dropdown'), [templateType])

  useMemo(() => {
    blocks.forEach(block => compiler.updateBlockTemplate(block))
  }, [blocks])

  const updateContent = useCallback(async () => {
    if (!disabled) {
      const preCompilerContent = await compiler.preCompileTemplate([blocksData || {}]);
      const content = preCompilerContent.compile({});
      const hasError = content.includes("error on render tamplate block")
      onChangeContent(hasError ? "" : content)
      onCompilerError(content.includes("error on render tamplate block"))
    }
  }, [disabled, blocksData, onChangeContent, onCompilerError])

  useEffect(() => {
    updateContent()
  }, [tcpaquestioncustom.blocks, updateContent])

  useEffect(() => {
    setCustomFieldsOptions((customFields || []).map(({
      id, group, type, options, date_format, key: value, name: label,
    }) => ({
      id, group, type, options, date_format, value, label: `${label} (${type})`,
    })))
  }, [customFields])

  useEffect(() => {
    setCustomFieldSelected(customFieldsOptions.reduce((accumulator, currentField) => {
      if (currentField.value === tcpaquestioncustom.cfid) {
        return currentField
      }
      return accumulator
    }, {}))
  }, [customFieldsOptions, tcpaquestioncustom.cfid])

  function handleChangeCustomField(customField) {
    onChange('cfid_number', customField ? `${customField.id}` : null);
    onChange('cfid', customField ? customField.value : null)
    tcpaquestioncustom.answers = []
    updateOptions(customField)
    onChange('answers', tcpaquestioncustom.answers)
    onChange('isMultipleAnswers', false)
    onChange('manualSubmit', false)
    setCustomFieldSelected(customField || {})
  }

  function insertAnswer(answers, answer) {
    answers.push(answer);
    return answers
  }

  function handleAddAnswer(answer) {
    onChange('answers', insertAnswer(tcpaquestioncustom.answers, answer))
  }

  function handleRemoveAnswers(idx) {
    const { answers } = tcpaquestioncustom
    answers.splice(idx, 1);
    onChange('answers', answers)
  }

  function updateOptions(customField) {
    if (!customField) return

    const isSelect = customField.type === 'select';
    const isBoolean = customField.type === 'boolean';

    if (isSelect) {
      const { answers } = tcpaquestioncustom
      const currentKeyAnswers = answers.map(({ key }) => key)
      const newKeysAnswers = customField.options.map(({ value: key }) => key)

      answers.forEach(({ key }, idx) => !newKeysAnswers.includes(key) && handleRemoveAnswers(idx))

      customField.options.forEach(({ value: key, label: answer }) => !currentKeyAnswers.includes(key) && handleAddAnswer({ key, answer }))
    }
    if (isBoolean) {
      const { answers } = tcpaquestioncustom;
      const options = [{ key: "true", answer: "Yes" }, { key: "false", answer: "No" }];
      const currentKeyAnswers = answers.map(({ key }) => key);
      answers.forEach(({ key }, idx) => {
        !["true", "false"].includes(key) && handleRemoveAnswers(idx)
      });
      options.forEach(({ key, answer }) => {
        !currentKeyAnswers.includes(key) && handleAddAnswer({ key, answer })
      });
      return;
    }
    tcpaquestioncustom.answers = tcpaquestioncustom.answers || [];
  }

  function handleCustomFieldEdit(cfid) {
    onCustomFieldClick({
      cfid,
      customFieldOpen: true,
    })
  }

  function handleCerts(attribute, checked) {
    if (checked) {
      if (!tcpaquestioncustom.cfid) return

      onChange(attribute, checked)
      onChange(`${attribute}_cfid`, `${tcpaquestioncustom.cfid}_${attribute}`)
    } else {
      onChange(attribute, false)
      onChange(`${attribute}_cfid`, '')
    }
  }

  function handleToggleOptions(attribute, checked) {
    switch (attribute) {
      case 'trustedform':
        handleCerts(attribute, checked)
        break;
      case 'leadid':
        handleCerts(attribute, checked)
        break;
      case 'isMultipleAnswers':
        onChange(attribute, checked)
        onChange('manualSubmit', false)
        break;
      case 'manualSubmit':
        onChange(attribute, checked)
        checked && onCustomFieldClick({ hideHostedSubmit: false })
        break;
      default:
        onChange(attribute, checked)
        break;
    }
  }

  return <div>
    <Row>
      <Col size="6">
        <FormGroup>
          <Label>Custom Field</Label>
          <Select
            name="type"
            value={tcpaquestioncustom.cfid || ''}
            options={customFieldsOptions}
            onChange={handleChangeCustomField}
            disabled={disabled}
          />
        </FormGroup>
      </Col>
      <Col size="4">
        <FormGroup>
          <Label>&nbsp;</Label>
          <ButtonGroup>
            <Button color="accent" disabled={disabled} onClick={() => handleCustomFieldEdit()}>
              <Plus size={14} />
              Add
            </Button>
            <DefaultTooltip content='Edit' triggerProps={{ asChild: true }}>
              <Button variant='outline' color="accent" disabled={disabled || !tcpaquestioncustom.cfid_number} onClick={() => handleCustomFieldEdit(tcpaquestioncustom.cfid_number)}>
                <Pencil size={14} />
              </Button>
            </DefaultTooltip>
          </ButtonGroup>
        </FormGroup>
      </Col>
    </Row>
    <Row className='mt-4'>
      <Col size='2'>
        <FormGroup>
          <Label>Trusted Form?</Label>
          <Switch
            onChange={e => handleToggleOptions('trustedform', e.target.checked)}
            checked={tcpaquestioncustom.trustedform || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>
      <Col size='2'>
        <FormGroup>
          <Label>LeadId?</Label>
          <Switch
            onChange={e => handleToggleOptions('leadid', e.target.checked)}
            checked={tcpaquestioncustom.leadid || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>
      <Col size='2'>
        <FormGroup>
          <Label>Always Visible?</Label>
          <Switch
            onChange={e => handleToggleOptions('alwaysShow', e.target.checked)}
            checked={tcpaquestioncustom.alwaysShow || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>
      <Col size='2'>
        <FormGroup>
          <Label>Consent text</Label>
          <Switch
            onChange={e => handleToggleOptions('consentTextActive', e.target.checked)}
            checked={tcpaquestioncustom.consentTextActive || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>
      <Col size='2'>
            <FormGroup>
              <Label>Auto Scroll to Consent</Label>
                <Switch
                  onChange={e => handleToggleOptions('autoScrollToConsent', e.target.checked)}
                  checked={tcpaquestioncustom.autoScrollToConsent || false}
                  disabled={disabled}
                />
            </FormGroup>
          </Col>
      {(customFieldSelected.type !== "boolean" && tcpaquestioncustom.templateType === 'only-buttons') && <Col size='2'>
        <FormGroup>
          <Label>Multiple answers</Label>
          <Switch
            onChange={e => handleToggleOptions('isMultipleAnswers', e.target.checked)}
            checked={tcpaquestioncustom.isMultipleAnswers}
            disabled={disabled}
          />
        </FormGroup>
      </Col>}
      {(tcpaquestioncustom.isMultipleAnswers || ['button-and-dropdown', 'only-dropdown'].includes(tcpaquestioncustom.templateType)) && <Col size='2'>
        <FormGroup>
          <Label>Required</Label>
          <Switch
            onChange={e => handleToggleOptions('required', e.target.checked)}
            checked={tcpaquestioncustom.required || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>}
      {(tcpaquestioncustom.templateType === "button-and-dropdown" || !tcpaquestioncustom.isMultipleAnswers) && <Col size='2'>
        <FormGroup>
          <Label>Manual Submit</Label>
          <Switch
            onChange={e => handleToggleOptions('manualSubmit', e.target.checked)}
            checked={tcpaquestioncustom.manualSubmit || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>}
      <Col size='2'>
        <FormGroup>
          <Label>Save Consent Text</Label>
          <Switch
            onChange={e => handleToggleOptions('saveConsentText', e.target.checked)}
            checked={tcpaquestioncustom.saveConsentText || false}
            disabled={disabled}
          />
        </FormGroup>
      </Col>
      <Col size="12">
        <FormGroup>
          <Label>Question</Label>
          <CodeEditor
            resizable
            height="100px"
            name="question"
            value={tcpaquestioncustom.question || ''}
            onChange={value => onChange('question', value)}
            disabled={disabled}
            className='h-[15vh]!'
          />
        </FormGroup>
      </Col>
      {tcpaquestioncustom.consentTextActive ? <>
        {allowBidSelection ? <>
          <Col size="12">
            <SimpleAlert color="warning">
              This integration requires using the helpers to render DBA's checkbox in your consent text.
            </SimpleAlert>
          </Col>
        </> : null}
        <>
          <Col size="12">
            <FormGroup>
              <Label className="flex justify-between items-center">
                Consent text
                {allowBidSelection ? <ConsentDBAs
                  data={tcpaquestioncustom.dbas}
                  onChange={data => onChange('dbas', data)}
                /> : null}
              </Label>
              <CodeEditor
                name="consentText"
                value={tcpaquestioncustom.consentText || ''}
                onChange={value => onChange('consentText', value)}
                disabled={disabled}
                resizable
                height="100px"
              />
            </FormGroup>
          </Col>
          <Col size="12">
            <FormGroup>
              <Label>Consent text position</Label>
              <Select
                value={tcpaquestioncustom.consentPosition}
                options={CONSENT_POSITION_TYPES}
                onChange={({ value }) => onChange('consentPosition', value)}
                disabled={disabled}
              />
            </FormGroup>
          </Col>
        </>
      </> : null}
      <TcpaQuestionCustomProvider />
    </Row>
  </div>
}
